/**
 * The mount lifecycle of one chart drawn by the blink-superset bundle: the React-root counterpart
 * of createEchartsLifecycle(), and the reason the two are separate.
 *
 * The bundle's charts are React components the bundle renders itself, so there is no echarts
 * instance here to reuse, resize or rebuild. What carries over is every guard that module pays
 * for — the render token, the zero-width wait, best-effort teardown — plus two this one needs:
 *
 *   - Never mount at width 0. EchartCore sizes its chart from the width and height it is handed,
 *     and 0 is a size, so a chart mounted into a box still display:none is drawn collapsed. After
 *     the frame budget a box that is still empty PARKS the draw, and the ResizeObserver mounts it
 *     the moment the box gets a width — where echarts-lifecycle.js draws regardless.
 *   - Resize by re-mounting. EchartCore has no observer of its own; it resizes when its width and
 *     height props change. Every re-mount is a fresh transform, so each one would replay the entry
 *     animation: resize re-mounts pass animation:false, and the size must hold still for
 *     RESIZE_SETTLE_MS first, so a drag does not re-render the chart per frame.
 *
 * Imports no bundle — the caller hands loadBundle in — and no echarts, so app.js can reach this
 * module without shipping either. SupersetChartModulesTest holds both.
 */
import { MAX_BOX_WAIT_FRAMES } from './echarts-lifecycle.js';
import { prefersMotion } from './cartesian-chrome.js';

const RESIZE_SETTLE_MS = 150;

/**
 * @param {object}   config
 * @param {string}   config.label         Names this chart in console messages.
 * @param {Function} config.getContainer  The element React mounts into.
 * @param {Function} [config.getRoot]     The element watched for visibility when `lazy`; the
 *                                        container itself is display:none until a chart state.
 * @param {Function} config.loadBundle    Resolves the loaded bundle.
 * @param {boolean}  [config.lazy]        Hold every draw until the root scrolls into view.
 * @param {boolean}  [config.inheritFont] Draw in the page's font — the root's computed font family —
 *                                        rather than Superset's.
 * @param {object}   [config.darkChrome]  The dashboards' dark chrome, for a chart on a dark card;
 *                                        every mount draws on the dark theme built from it.
 * @param {boolean}  [config.animate]     false never plays the entry animation, for a surface that
 *                                        redraws on every change of what the reader picked and has
 *                                        always drawn still (the scorecard drawer's sparkline).
 *                                        Left true, a first draw animates unless the reader asked
 *                                        for reduced motion.
 * @param {Function} config.onDrawn       (mount, bundle, contract) after each first-time draw of a
 *                                        contract; not called for resize re-mounts.
 * @param {Function} config.onError       (error, stage) when loading or mounting throws, after teardown.
 *                                        stage is 'load' when the bundle itself did not load, which no
 *                                        later draw in this page can recover (superset-bundle.js), and
 *                                        'draw' when it loaded and the chart did not mount.
 */
export function createSupersetChartLifecycle({ label, getContainer, getRoot = getContainer, loadBundle, lazy = false, darkChrome = null, inheritFont = false, animate = true, onDrawn, onError }) {
    let token = 0;
    let bundle = null;
    let mountedOn = null;
    let drawn = null;
    let lastSize = { width: 0, height: 0 };
    let waitingForView = null;
    let waitingForBox = null;
    let visible = ! lazy;
    let resizeObserver = null;
    let viewObserver = null;
    let resizeTimer = null;

    const firstDrawMotion = () => animate && prefersMotion();

    const measure = (container) => ({
        width: Math.floor(container.clientWidth),
        height: Math.floor(container.clientHeight),
    });

    function unmount() {
        if (mountedOn && bundle) {
            try {
                bundle.unmountBlinkChart(mountedOn);
            } catch (error) {
                console.debug(`unmountBlinkChart failed during ${label} cleanup:`, error);
            }
        }
        mountedOn = null;
    }

    function fail(job, error, stage = 'draw') {
        if (job.token !== token) return;
        unmount();
        drawn = null;
        onError(error, stage);
    }

    function mount(job, container, animation, isResize) {
        const size = measure(container);

        try {
            const result = bundle.mountBlinkChart(container, job.contract, {
                ...size,
                animation,
                ...(job.tooltip ? { tooltip: job.tooltip } : {}),
                ...(job.styleColors ? { styleColors: job.styleColors } : {}),
                ...(job.onPeriodClick ? { onPeriodClick: job.onPeriodClick } : {}),
                ...(darkChrome ? { darkChrome } : {}),
                ...(inheritFont ? { fontFamily: getComputedStyle(getRoot()).fontFamily } : {}),
            });

            mountedOn = container;
            drawn = job;
            lastSize = size;

            if (! isResize) onDrawn(result, bundle, job.contract);
        } catch (error) {
            fail(job, error);
        }
    }

    function onResize() {
        const container = getContainer();
        if (! container) return;

        const size = measure(container);
        if (size.width === 0) return;

        if (waitingForBox) {
            const job = waitingForBox;
            waitingForBox = null;
            if (job.token === token) mount(job, container, firstDrawMotion(), false);
            return;
        }

        if (! drawn || drawn.token !== token) return;
        if (Math.abs(size.width - lastSize.width) < 1 && Math.abs(size.height - lastSize.height) < 1) return;

        mount(drawn, container, false, true);
    }

    function observeSize(container) {
        if (resizeObserver || typeof ResizeObserver === 'undefined') return;

        resizeObserver = new ResizeObserver(() => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(onResize, RESIZE_SETTLE_MS);
        });
        resizeObserver.observe(container);
    }

    function waitForBox(job, attempt = 0) {
        if (job.token !== token) return;

        const container = getContainer();
        if (! container) return;

        // Observed before the first mount, not after it: the observer is also how a box that is
        // still empty when the frame budget runs out gets its chart.
        observeSize(container);

        if (container.clientWidth === 0) {
            if (attempt < MAX_BOX_WAIT_FRAMES) {
                requestAnimationFrame(() => waitForBox(job, attempt + 1));
            } else {
                waitingForBox = job;
            }
            return;
        }

        mount(job, container, firstDrawMotion(), false);
    }

    function run(job) {
        Promise.resolve()
            .then(() => loadBundle())
            .then(
                (loaded) => {
                    bundle = loaded;
                    waitForBox(job);
                },
                (error) => fail(job, error, 'load'),
            )
            .catch((error) => fail(job, error));
    }

    function watchView() {
        if (viewObserver) return;

        // No IntersectionObserver means no way to tell, so draw rather than never.
        if (typeof IntersectionObserver === 'undefined') {
            visible = true;
            return;
        }

        const root = getRoot();
        if (! root) return;

        viewObserver = new IntersectionObserver((entries) => {
            if (! entries.some((entry) => entry.isIntersecting)) return;

            visible = true;
            viewObserver.disconnect();
            viewObserver = null;

            if (waitingForView) {
                const job = waitingForView;
                waitingForView = null;
                run(job);
            }
        }, { threshold: 0.1 });
        viewObserver.observe(root);
    }

    function clear() {
        token += 1;
        drawn = null;
        waitingForView = null;
        waitingForBox = null;
        clearTimeout(resizeTimer);
        unmount();
    }

    return {
        /**
         * Draw a contract, replacing whatever this chart drew before. The latest call wins: an
         * earlier one still waiting on the bundle, a box or the viewport is dropped by token.
         * `onPeriodClick` (instant) is the bundle's hook for a click on the plot, handed the period
         * the tooltip shows; it is passed again on every resize re-mount, which keeps it wired.
         */
        draw(contract, { tooltip = null, styleColors = null, onPeriodClick = null } = {}) {
            token += 1;
            const job = { contract, tooltip, styleColors, onPeriodClick, token };

            if (! visible) {
                waitingForView = job;
                watchView();
                if (! visible) return;
                waitingForView = null;
            }

            run(job);
        },

        /** Drop any draw in flight and take the chart down — the empty, failed and loading paths. */
        clear,

        cleanup() {
            clear();
            resizeObserver?.disconnect();
            resizeObserver = null;
            viewObserver?.disconnect();
            viewObserver = null;
        },
    };
}
